/**
 * Saves / restores lightweight work state across inactivity logouts.
 * Does not store tokens or passwords.
 */
const WORK_STATE_PREFIX = 'dth_work_resume_';

/** Known in-app draft keys to capture automatically. */
const KNOWN_SNAPSHOT_KEYS = [
  'sr-register-wizard-snapshot',
];

function storageKey(userId) {
  return `${WORK_STATE_PREFIX}${String(userId || '').trim()}`;
}

function safeParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function captureKnownSnapshots() {
  if (typeof window === 'undefined') return {};
  const snapshots = {};
  KNOWN_SNAPSHOT_KEYS.forEach((key) => {
    const value = window.sessionStorage.getItem(key);
    if (value != null) snapshots[key] = value;
  });
  // Also capture any keys that modules registered via custom event buffer.
  const extras = window.__dthExtraWorkSnapshots;
  if (extras && typeof extras === 'object') {
    Object.assign(snapshots, extras);
  }
  return snapshots;
}

/**
 * Persist current route + optional snapshots before forced logout.
 */
export function saveWorkState({ userId, tenantId, path, snapshots, meta } = {}) {
  if (typeof window === 'undefined' || !userId) return null;
  const payload = {
    userId: String(userId),
    tenantId: tenantId ? String(tenantId) : null,
    path: path || `${window.location.pathname}${window.location.search}${window.location.hash}`,
    snapshots: snapshots || captureKnownSnapshots(),
    meta: meta || {},
    savedAt: Date.now(),
    reason: 'inactivity',
  };
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(payload));
  } catch (e) {
    console.warn('Failed to save work state', e);
    return null;
  }
  return payload;
}

export function loadWorkState(userId) {
  if (typeof window === 'undefined' || !userId) return null;
  const raw = window.localStorage.getItem(storageKey(userId));
  if (!raw) return null;
  const parsed = safeParse(raw);
  if (!parsed || parsed.userId !== String(userId)) return null;
  // Drop states older than 24h.
  if (parsed.savedAt && Date.now() - parsed.savedAt > 24 * 60 * 60 * 1000) {
    clearWorkState(userId);
    return null;
  }
  return parsed;
}

export function clearWorkState(userId) {
  if (typeof window === 'undefined' || !userId) return;
  window.localStorage.removeItem(storageKey(userId));
}

export function restoreWorkSnapshots(workState) {
  if (typeof window === 'undefined' || !workState?.snapshots) return;
  Object.entries(workState.snapshots).forEach(([key, value]) => {
    if (value == null) return;
    try {
      window.sessionStorage.setItem(key, String(value));
    } catch (e) {
      console.warn('Failed to restore snapshot', key, e);
    }
  });
}

/**
 * Allow feature modules to contribute extra snapshots before idle logout.
 * Example: window.dispatchEvent(new CustomEvent('dth:register-work-snapshot', { detail: { key, value }}))
 */
export function installWorkSnapshotCollector() {
  if (typeof window === 'undefined') return () => {};
  if (!window.__dthExtraWorkSnapshots) {
    window.__dthExtraWorkSnapshots = {};
  }
  const onRegister = (event) => {
    const { key, value } = event.detail || {};
    if (!key) return;
    if (value == null) {
      delete window.__dthExtraWorkSnapshots[key];
    } else {
      window.__dthExtraWorkSnapshots[key] = value;
    }
  };
  window.addEventListener('dth:register-work-snapshot', onRegister);
  return () => window.removeEventListener('dth:register-work-snapshot', onRegister);
}
